import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { toast } from 'react-toastify';
import {
  Alert,
  Box,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DifferenceOutlinedIcon from '@mui/icons-material/DifferenceOutlined';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import RestoreIcon from '@mui/icons-material/Restore';
import RestorePageOutlinedIcon from '@mui/icons-material/RestorePageOutlined';
import { Button } from 'components/index';
import { usePrompt } from '../../../AppPrompt';
import { IconOverTextButton } from '../../../common/IconOverTextButton';
import { ButtonDivider } from '../../../common/ButtonDivider';
import { useMagicReport } from '../context/MagicReportContext';
import {
  getMagicReportVersion,
  getMagicReportVersions,
  nameMagicReportVersion,
  removeMagicReportVersionName,
  restoreMagicReportVersion,
  restoreMagicReportVersionChanges,
} from '../magic-report.api';
import { versionedStateFromReport } from '../magic-report-state.util';
import { isReportChangedError, REPORT_CHANGED_MESSAGE } from '../../shared/utils/reportChangedError';
import VersionList, { VersionSelection } from './VersionList';
import VersionChanges from './VersionChanges';
import { dateTimeLabel, labelForKind, labelForStep, labelsForKeys, parseUtc, timeLabel } from './version-labels';
import { FIRST_CHANGE_ATTRIBUTE, highlightChanges } from './html-diff.util';
import { replaceUnavailableImages } from './image-availability.util';
import { VERSION_PHOTOS } from './version-history.config';
import { MagicReportRevisionPreview } from '../magic-report.type';

// The report HTML is laid out for print: its margins come from @page rules, which a browser preview
// ignores, so on screen the text would run to the edges. Show each page as a sheet with margins.
const PREVIEW_SCREEN_STYLES = `
  @media screen {
    html, body { background: #f5f5f5 !important; }
    body { padding: 24px 32px !important; }
    .page { padding: 56px 64px !important; margin: 0 auto 24px !important; box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15); }
  }
`;

const withPreviewLayout = (html: string) =>
  html.includes('</head>')
    ? html.replace('</head>', `<style>${PREVIEW_SCREEN_STYLES}</style></head>`)
    : `<style>${PREVIEW_SCREEN_STYLES}</style>${html}`;

/** The editor reads the report once, when it opens; reload it (without the history view) to pick up a restore. */
const reloadEditor = () => {
  const url = new URL(window.location.href);
  url.searchParams.delete('history');
  // Reload in place rather than navigating, so the router state the report was opened with
  // survives. Reports without stored draw ids get their draw only from that state; navigating
  // dropped it and the editor reopened with no draws, no tables and no working step navigation.
  window.history.replaceState(window.history.state, '', url.toString());
  window.location.reload();
};

const showActionError = (error: unknown, fallback: string) => {
  console.error(fallback, error);
  if (isReportChangedError(error)) toast.error(REPORT_CHANGED_MESSAGE, { autoClose: false });
  else toast.error(error instanceof Error && error.message ? error.message : fallback);
};

const NameVersionDialog: React.FC<{
  open: boolean;
  initialName: string;
  onClose: () => void;
  onSave: (name: string) => Promise<void>;
  onRemove: () => Promise<void>;
}> = ({ open, initialName, onClose, onSave, onRemove }) => {
  const [name, setName] = useState(initialName);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setName(initialName);
  }, [open, initialName]);

  const save = async () => {
    setSaving(true);
    try {
      await onSave(name.trim());
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth='xs'>
      <DialogTitle>{initialName ? 'Rename this version' : 'Name this version'}</DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          fullWidth
          margin='dense'
          label='Name'
          placeholder='e.g. Sent to lender'
          value={name}
          inputProps={{ maxLength: 200 }}
          onChange={event => setName(event.target.value)}
          onKeyDown={event => {
            if (event.key === 'Enter' && name.trim()) save();
          }}
        />
        <Typography variant='caption' color='text.secondary'>
          Named versions are kept permanently.
        </Typography>
      </DialogContent>
      <DialogActions>
        {initialName && (
          <Button
            variant='text'
            color='error'
            sx={{ mr: 'auto' }}
            disabled={saving}
            onClick={async () => {
              setSaving(true);
              try {
                await onRemove();
              } finally {
                setSaving(false);
              }
            }}
          >
            Remove name
          </Button>
        )}
        <Button variant='text' onClick={onClose}>
          Cancel
        </Button>
        <Button variant='contained' onClick={save} disabled={!name.trim() || saving}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface VersionHistoryViewProps {
  reportId: string;
  onClose: () => void;
}

/**
 * Read-only version history, laid out like Google Docs: the selected version rendered on the left,
 * the list of versions on the right. Project figures, invoices and recap applications always show
 * current data — only the report's own content is versioned.
 */
const VersionHistoryView: React.FC<VersionHistoryViewProps> = ({ reportId, onClose }) => {
  const { reportState, api, isDirty, hasUnsavedPhotoChanges, saveReport, isSaving } = useMagicReport() as any;
  const { getConfirmation } = usePrompt();
  const [isApplying, setIsApplying] = useState(false);
  const [nameDialogOpen, setNameDialogOpen] = useState(false);
  const [namedOnly, setNamedOnly] = useState(false);
  const [selection, setSelection] = useState<VersionSelection>();
  const selectedRevisionId = selection?.revisionId;
  const [showChanges, setShowChanges] = useState(true);
  const [html, setHtml] = useState<string>();
  const [renderError, setRenderError] = useState<string>();
  // Rendered HTML per revision, so stepping through versions doesn't rebuild the same report twice.
  const renderedRef = useRef(new Map<string, Promise<string>>());

  const {
    data: versions,
    isLoading: isLoadingVersions,
    refetch: refetchVersions,
  } = useQuery(['magicReportVersions', reportId, namedOnly], () => getMagicReportVersions(reportId, namedOnly), {
    cacheTime: 0,
    staleTime: 0,
  });

  useEffect(() => {
    // Open on the latest session, showing everything it changed.
    if (!selection && versions?.currentRevisionId) setSelection({ revisionId: versions.currentRevisionId, wholeSession: true });
  }, [versions, selection]);

  // A selected session with several versions is compared as a whole: its latest version against
  // the version before the session started.
  const selectedSession = selection?.wholeSession
    ? versions?.sessions.find(session => session.latestRevisionId === selection.revisionId && session.revisions.length > 1)
    : undefined;
  const allRevisions = (versions?.sessions ?? []).flatMap(session => session.revisions);
  const revisionBefore = (revisionNumber: number) =>
    allRevisions.filter(r => r.revisionNumber < revisionNumber).sort((a, b) => b.revisionNumber - a.revisionNumber)[0]?.id;

  const {
    data: preview,
    isLoading: isLoadingPreview,
    refetch: refetchPreview,
  } = useQuery(['magicReportVersion', reportId, selectedRevisionId], () => getMagicReportVersion(reportId, selectedRevisionId as string), {
    enabled: !!selectedRevisionId,
    cacheTime: 5 * 60 * 1000,
  });

  // What the selection is compared with: the version before the session for a whole session, else
  // the version just before. The server names the latter; the full (unfiltered) list is a fallback
  // for older servers that don't.
  const sessionStartRevision = selectedSession?.revisions[selectedSession.revisions.length - 1];
  const previousRevisionId = sessionStartRevision
    ? // A session that opens with the starting point has nothing before it: compare with the starting point.
      sessionStartRevision.kind === 'Baseline'
      ? sessionStartRevision.id
      : revisionBefore(sessionStartRevision.revisionNumber)
    : (preview?.previousRevisionId ?? (preview && !namedOnly ? revisionBefore(preview.revision.revisionNumber) : undefined));
  const { data: previousPreview, isError: isPreviousError } = useQuery(
    ['magicReportVersion', reportId, previousRevisionId],
    () => getMagicReportVersion(reportId, previousRevisionId as string),
    { enabled: showChanges && !!previousRevisionId, cacheTime: 5 * 60 * 1000 },
  );

  // Render a version through the same HTML builder as the Preview step: current project data with
  // the version's own content and photos laid over it. reportState is read when a version is first
  // rendered; re-rendering on every editor change would be wasted work here.
  const renderVersion = (version: MagicReportRevisionPreview) => {
    const cached = renderedRef.current.get(version.revision.id);
    if (cached) return cached;

    const fallbackDrawId = reportState.draws?.[0]?.drawId;
    const versionState = { ...reportState, ...versionedStateFromReport(version.report, fallbackDrawId) };
    const photos = (version.report.photos || []).map((photo: any) => ({
      ...photo,
      reportId,
      blobName: photo.pdfBlobName || photo.blobName || photo.originalBlobName,
    }));
    // Photos aren't versioned yet: leave the photographs pages out (cover photos show as they are now).
    const rendered: Promise<string> = api.buildReportHtml(versionState, VERSION_PHOTOS ? { photos } : { omitPhotosPage: true });
    renderedRef.current.set(version.revision.id, rendered);
    rendered.catch(() => renderedRef.current.delete(version.revision.id));
    return rendered;
  };

  const waitingForPrevious = showChanges && !!previousRevisionId && !previousPreview && !isPreviousError;

  useEffect(() => {
    if (!preview || waitingForPrevious) return;
    let cancelled = false;
    setHtml(undefined);
    setRenderError(undefined);

    (async () => {
      try {
        const current = await renderVersion(preview);
        const marked = showChanges && previousPreview ? highlightChanges(current, await renderVersion(previousPreview)).html : current;
        const shown = await replaceUnavailableImages(marked);
        if (!cancelled) setHtml(withPreviewLayout(shown));
      } catch (error) {
        console.error('Failed to render version', error);
        if (!cancelled) setRenderError('This version could not be displayed.');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [preview, previousPreview, showChanges, waitingForPrevious]);

  const revision = preview?.revision;
  const revisionTime = parseUtc(revision?.lastAmendedOn);
  const selectedTime = parseUtc(allRevisions.find(r => r.id === selectedRevisionId)?.lastAmendedOn);
  const versionLabel = selectedTime ? dateTimeLabel(selectedTime) : 'this version';
  const hasUnsavedChanges = isDirty || hasUnsavedPhotoChanges;
  // The buttons go by the row selected in the list, so they don't switch off while its preview loads.
  const selectedSummary = allRevisions.find(r => r.id === selectedRevisionId);
  const isCurrentVersion = !!selectedRevisionId && selectedRevisionId === versions?.currentRevisionId;
  const selectionChangedKeys = selectedSession?.changedKeys ?? selectedSummary?.changedKeys ?? [];
  const selectionLabel = selectedSession ? 'this session' : 'this version';
  // The restore buttons are always shown; when they can't be used, the tooltip says why.
  const busyReason = isApplying || isSaving ? 'Please wait…' : hasUnsavedChanges ? 'Save your changes first.' : undefined;
  const restoreBlockedReason = !selectedSummary ? 'Select a version.' : isCurrentVersion ? 'This is the current version.' : busyReason;
  const restoreChangesBlockedReason = !selectedSummary
    ? 'Select a version.'
    : labelsForKeys(selectionChangedKeys).length === 0
      ? `${selectedSession ? 'This session has' : 'This version has'} no changes to restore.`
      : isCurrentVersion
        ? `The report already has ${selectedSession ? 'this session’s' : 'this version’s'} changes.`
        : busyReason;

  // Puts back what the selection changed (what is highlighted), as it left it — even where later
  // versions changed the same sections — and leaves everything else in the report as it is now.
  const handleRestoreChanges = async () => {
    if (!selectedSummary) return;
    const labels = labelsForKeys(selectionChangedKeys);
    const changed = labels.map(label => `<li style="break-inside: avoid;">${label}</li>`).join('');
    // A long list goes into two columns so the dialog stays short.
    const listStyle = labels.length > 6 ? ' style="column-count: 2; column-gap: 32px;"' : '';
    const confirmed = await getConfirmation({
      heading: `Restore ${selectionLabel}’s changes?`,
      description: `These sections go back to how ${selectionLabel} left them:<ul${listStyle}>${changed}</ul>Later edits to these sections are replaced, and the rest of the report stays as it is now. The current version stays in the history, so you can undo this.`,
      confirmButton: { label: 'Restore changes', color: 'primary' },
      cancelButton: { label: 'Cancel', color: 'secondary' },
    });
    if (!confirmed) return;

    setIsApplying(true);
    try {
      const sessionStart = selectedSession?.revisions[selectedSession.revisions.length - 1];
      await restoreMagicReportVersionChanges(reportId, selectedSummary.id, { overwriteConflicts: true, sinceRevisionId: sessionStart?.id });
      toast.success('Changes restored. Reloading the report…');
      reloadEditor();
    } catch (error) {
      showActionError(error, 'Failed to restore these changes.');
      setIsApplying(false);
    }
  };

  const handleRestore = async () => {
    if (!selectedSummary) return;
    const confirmed = await getConfirmation({
      heading: 'Restore this version?',
      description: `The whole report goes back to how it was on ${versionLabel}.<ul><li>Everything changed since then is undone.</li><li>Draw, budget and line-item figures and invoices are not changed.</li><li>The current version stays in the history, so you can undo this.</li></ul>`,
      confirmButton: { label: 'Restore', color: 'primary' },
      cancelButton: { label: 'Cancel', color: 'secondary' },
    });
    if (!confirmed) return;

    setIsApplying(true);
    try {
      await restoreMagicReportVersion(reportId, selectedSummary.id);
      toast.success('Version restored. Reloading the report…');
      reloadEditor();
    } catch (error) {
      showActionError(error, 'Failed to restore this version.');
      setIsApplying(false);
    }
  };

  const handleSaveName = async (name: string) => {
    if (!revision) return;
    try {
      await nameMagicReportVersion(reportId, revision.id, name);
      setNameDialogOpen(false);
      await Promise.all([refetchVersions(), refetchPreview()]);
    } catch (error) {
      showActionError(error, 'Failed to name this version.');
    }
  };

  const handleRemoveName = async () => {
    if (!revision) return;
    try {
      await removeMagicReportVersionName(reportId, revision.id);
      setNameDialogOpen(false);
      await Promise.all([refetchVersions(), refetchPreview()]);
    } catch (error) {
      showActionError(error, 'Failed to remove the name.');
    }
  };
  const sessionStart = parseUtc(selectedSession?.startedOn);
  const heading = !revision
    ? 'Version history'
    : selectedSession
      ? [
          revision.name,
          sessionStart && revisionTime && `${dateTimeLabel(sessionStart)} – ${timeLabel(revisionTime)}`,
          `${selectedSession.revisions.length} versions`,
        ]
          .filter(Boolean)
          .join(' · ')
      : [revision.name, revisionTime && dateTimeLabel(revisionTime), labelForKind(revision.kind), labelForStep(revision.step)]
          .filter(Boolean)
          .join(' · ');

  return (
    <Dialog fullScreen open onClose={onClose} aria-labelledby='version-history-title'>
      <Stack sx={{ height: '100%' }}>
        {/* Top bar: the version and its actions, then a strip with what changed */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'background.paper' }}>
          <Stack direction='row' alignItems='center' spacing={2} sx={{ px: 3, minHeight: 64 }}>
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography id='version-history-title' variant='subtitle1' sx={{ fontWeight: 600, lineHeight: 1.3 }} noWrap>
                {heading}
              </Typography>
              {revision?.author?.name && (
                <Typography variant='body2' color='text.secondary' noWrap>
                  {revision.author.name}
                </Typography>
              )}
            </Box>
            <Stack direction='row' alignItems='center' spacing={0} sx={{ flexShrink: 0 }}>
              <IconOverTextButton
                icon={<DifferenceOutlinedIcon />}
                text={showChanges ? 'Hide changes' : 'Display changes'}
                tooltip={
                  showChanges
                    ? 'Showing what changed compared with the version before. Click to hide.'
                    : 'Highlight what changed compared with the version before.'
                }
                onClick={() => setShowChanges(prev => !prev)}
                sunken={showChanges}
              />
              <IconOverTextButton
                icon={<LabelOutlinedIcon />}
                text={revision?.name ? 'Rename' : 'Name'}
                tooltip={
                  revision?.name ? 'Rename or remove the name of this version' : 'Name this version. Named versions are kept permanently.'
                }
                onClick={() => setNameDialogOpen(true)}
                disabled={!revision || isApplying}
                wrapInSpan
              />
              <ButtonDivider />
              <IconOverTextButton
                icon={<RestorePageOutlinedIcon />}
                text='Restore changes'
                tooltip={
                  restoreChangesBlockedReason ??
                  `Bring back only what ${selectionLabel} changed (highlighted in yellow), even if it was edited again later. Everything else stays as it is now.`
                }
                onClick={handleRestoreChanges}
                disabled={!!restoreChangesBlockedReason}
                wrapInSpan
              />
              <IconOverTextButton
                icon={<RestoreIcon />}
                text='Restore all'
                tooltip={
                  restoreBlockedReason ?? 'Put the entire report back the way it was at this point, undoing everything changed since.'
                }
                onClick={handleRestore}
                disabled={!!restoreBlockedReason}
                wrapInSpan
              />
              <ButtonDivider />
              <IconOverTextButton
                icon={<ArrowBackIcon />}
                text='Back to editing'
                tooltip='Close version history and go back to the report'
                onClick={onClose}
                disabled={isApplying}
                color='primary'
                wrapInSpan
              />
            </Stack>
          </Stack>

          <Stack
            direction='row'
            alignItems='center'
            spacing={2}
            sx={{ px: 3, py: 0.75, minHeight: 44, bgcolor: 'grey.50', borderTop: 1, borderColor: 'divider' }}
          >
            <Stack direction='row' alignItems='center' spacing={1} sx={{ flex: 1, minWidth: 0 }}>
              {showChanges && preview && (
                <>
                  <Typography
                    variant='caption'
                    color='text.secondary'
                    sx={{ fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5, flexShrink: 0 }}
                  >
                    {selectedSession ? 'Changed in this session' : 'Changed in this version'}
                  </Typography>
                  <VersionChanges changedKeys={selectedSession ? selectedSession.changedKeys : preview.changes.map(change => change.key)} />
                </>
              )}
            </Stack>
            <Stack direction='row' alignItems='center' spacing={0.75} sx={{ flexShrink: 0, color: 'text.secondary' }}>
              <InfoOutlinedIcon sx={{ fontSize: 16 }} />
              <Typography variant='caption'>Draw, budget and line-item figures and invoices always show current numbers.</Typography>
            </Stack>
          </Stack>
        </Box>

        <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
          {/* The selected version */}
          <Stack sx={{ flex: 1, minWidth: 0, p: 2, gap: 2, overflow: 'auto', bgcolor: 'grey.100' }}>
            {hasUnsavedChanges && (
              <Alert
                severity='warning'
                action={
                  <Button
                    variant='text'
                    disabled={isSaving}
                    onClick={async () => {
                      await saveReport();
                      refetchVersions();
                    }}
                  >
                    {isSaving ? 'Saving…' : 'Save now'}
                  </Button>
                }
              >
                You have unsaved changes. Save them before restoring or undoing a version.
              </Alert>
            )}

            <Box sx={{ flex: 1, minHeight: 600, bgcolor: 'grey.100', borderRadius: 1, position: 'relative' }}>
              {renderError ? (
                <Alert severity='error' sx={{ m: 2 }}>
                  {renderError}
                </Alert>
              ) : html ? (
                <iframe
                  title='Report version preview'
                  srcDoc={html}
                  sandbox='allow-same-origin'
                  onLoad={event => {
                    if (!showChanges) return;
                    event.currentTarget.contentDocument?.querySelector(`[${FIRST_CHANGE_ATTRIBUTE}]`)?.scrollIntoView({ block: 'center' });
                  }}
                  style={{ width: '100%', height: '100%', minHeight: 600, border: 'none', display: 'block' }}
                />
              ) : selectedRevisionId || isLoadingPreview ? (
                <Stack alignItems='center' justifyContent='center' sx={{ height: '100%', minHeight: 300 }}>
                  <CircularProgress />
                </Stack>
              ) : null}
            </Box>
          </Stack>

          <VersionList
            versions={versions}
            isLoading={isLoadingVersions}
            selection={selection}
            onSelect={setSelection}
            namedOnly={namedOnly}
            onNamedOnlyChange={setNamedOnly}
          />
        </Box>
      </Stack>
      <NameVersionDialog
        open={nameDialogOpen}
        initialName={revision?.name ?? ''}
        onClose={() => setNameDialogOpen(false)}
        onSave={handleSaveName}
        onRemove={handleRemoveName}
      />
    </Dialog>
  );
};

export default VersionHistoryView;
